

/**
 * Workspaces page component - Display Workspaces with Real API Data
 * Connected to backend API for real-time workspace management
 */

import React, { useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import { useWorkspaceStatistics } from '../hooks';
import { workspaceService } from '../services/workspace-service';
import { WorkspaceFilters } from '../components/WorkspaceFilters';
import { WorkspaceTable } from '../components/WorkspaceTable';
import { WorkspaceEmptyState, WorkspaceLoadingSkeleton, WorkspaceErrorState } from '../components/empty-states/WorkspaceEmptyStates';
import type { WorkspaceFilterOptions } from '../types';

export function Workspaces() {
  const queryClient = useQueryClient();
  const [currentPageToken, setCurrentPageToken] = useState<string | undefined>(undefined);
  const [pageHistory, setPageHistory] = useState<(string | undefined)[]>([undefined]);
  const [currentPageIndex, setCurrentPageIndex] = useState(0);
  const [filters, setFilters] = useState<WorkspaceFilterOptions>({});
  const itemsPerPage = 100;

  const [viewingWorkspaceId, setViewingWorkspaceId] = useState<string | null>(null);
  const [viewingWorkspace, setViewingWorkspace] = useState<any | null>(null);
  const [viewLoading, setViewLoading] = useState(false);

  // Use getWorkspacesAccumulate for paginated workspace data
  const [workspaces, setWorkspaces] = useState<any[]>([]);
  const [hasNextPage, setHasNextPage] = useState(false);
  const [nextPageToken, setNextPageToken] = useState<string | undefined>(undefined);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<any>(null);
  const { data: stats, isLoading: statsLoading, error: statsError } = useWorkspaceStatistics(filters);

  // Fetch workspaces recursively for each page
  React.useEffect(() => {
    let cancelled = false;
    async function fetchData() {
      setIsLoading(true);
      setError(null);
      try {
        const resp = await workspaceService.getWorkspacesAccumulate({
          limit: itemsPerPage,
          nextToken: currentPageToken
        }, filters);
        if (!cancelled) {
          setWorkspaces(resp.items || []);
          setHasNextPage(resp.hasMore || false);
          setNextPageToken(resp.nextToken);
        }
      } catch (err) {
        if (!cancelled) setError(err);
      } finally {
        if (!cancelled) setIsLoading(false);
      }
    }
    fetchData();
    return () => { cancelled = true; };
  }, [currentPageToken, filters]);

  React.useEffect(() => {
    let cancelled = false;
    async function loadWorkspace() {
      if (!viewingWorkspaceId) return;
      setViewLoading(true);
      try {
        const data = await workspaceService.getWorkspace(viewingWorkspaceId);
        if (!cancelled) setViewingWorkspace(data);
      } catch (e) {
        if (!cancelled) setViewingWorkspace(null);
      } finally {
        if (!cancelled) setViewLoading(false);
      }
    }
    loadWorkspace();
    return () => { cancelled = true; };
  }, [viewingWorkspaceId]);


  const handleFiltersChange = (newFilters: WorkspaceFilterOptions) => {
    setFilters(newFilters);
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handleClearFilters = () => {
    setFilters({});
    setCurrentPageToken(undefined);
    setPageHistory([undefined]);
    setCurrentPageIndex(0);
  };

  const handlePreviousPage = () => {
    if (currentPageIndex > 0) {
      queryClient.removeQueries({ 
        queryKey: ['workspaces', 'page'],
        exact: false 
      });
      
      const prevIndex = currentPageIndex - 1;
      setCurrentPageIndex(prevIndex);
      setCurrentPageToken(pageHistory[prevIndex]);
    }
  };

  const handleNextPage = () => {
    if (nextPageToken) {
      queryClient.removeQueries({ 
        queryKey: ['workspaces', 'page'],
        exact: false 
      });
      
      const newIndex = currentPageIndex + 1;
      
      if (pageHistory.length <= newIndex) {
        setPageHistory(prev => [...prev, nextPageToken]);
      } else {
        setPageHistory(prev => {
          const updated = [...prev];
          updated[newIndex] = nextPageToken;
          return updated.slice(0, newIndex + 1); // Trim any extra tokens
        });
      }
      
      setCurrentPageIndex(newIndex);
      setCurrentPageToken(nextPageToken);
    }
  };

  if (isLoading) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">Workspace Management</h1>
          <p className="content-subtitle">View and manage all workspaces in your multi-tenant SaaS platform</p>
        </div>
        <div className="animate-pulse space-y-4">
          <div className="h-32 bg-gray-200 rounded-lg"></div>
          <div className="h-32 bg-gray-200 rounded-lg"></div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="space-y-6">
        <div className="content-header">
          <h1 className="content-title">Workspace Management</h1>
          <p className="content-subtitle">View and manage all workspaces in your multi-tenant SaaS platform</p>
        </div>
        <div className="bg-white border border-gray-200 rounded-lg shadow-sm">
          <div className="text-center py-8">
            <p className="text-red-600">Error loading workspaces</p>
            <p className="text-sm text-gray-500 mt-2">
              {error instanceof Error ? error.message : 'Unable to load workspace data'}
            </p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col h-full">
      {/* Content Header */}
      <div className="content-header flex-shrink-0">
        <div className="flex justify-between items-start">
          <div>
            <h1 className="content-title">Workspace Management</h1>
            <p className="content-subtitle">View and manage all workspaces in your multi-tenant SaaS platform</p>
          </div>
        </div>
      </div>

      {/* Statistics Overview */}
      <div className="dashboard-metrics flex-shrink-0">
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.total || 0)}
          </div>
          <div className="metric-label">Total Tenants</div>
        </div>
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.unknown || 0)}
          </div>
          <div className="metric-label">Unknown</div>
        </div>
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.eu || 0)}
          </div>
          <div className="metric-label">EU</div>
        </div>
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.us || 0)}
          </div>
          <div className="metric-label">US</div>
        </div>
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.uk || 0)}
          </div>
          <div className="metric-label">UK</div>
        </div>
        <div className="metrics-card">
          <div className={`metric-value ${statsLoading ? 'animate-pulse bg-gray-200 rounded h-6 w-12' : ''}`}>
            {statsLoading ? '' : (stats?.apac || 0)}
          </div>
          <div className="metric-label">APAC</div>
        </div>
      </div>

      {/* Workspace Filters */}
      <WorkspaceFilters
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
      />

      {/* Filter Status Indicator */}
      {Object.values(filters).some(value => 
        value !== undefined && value !== null && value !== ''
      ) && (
        <div className="mb-4">
          <div className="text-sm text-blue-600 font-medium">
            Filters Active
            {(() => {
              const activeFilters = [];
              if (filters.tenantId) activeFilters.push(`Tenant ID: "${filters.tenantId}"`);
              if (filters.accessEvoRegion) activeFilters.push(`Region: ${filters.accessEvoRegion}`);
              if (filters.applicationSubscriptionId) activeFilters.push(`Application Subscription ID: "${filters.applicationSubscriptionId}"`);
              return activeFilters.length > 0 ? ` (${activeFilters.join(', ')})` : '';
            })()}
          </div>
        </div>
      )}

      {/* Workspace Table Card */}
      <div className="card">
        {isLoading ? (
          <WorkspaceLoadingSkeleton />
        ) : error ? (
          <WorkspaceErrorState 
            onRetry={() => window.location.reload()}
            error={typeof error === 'string' ? error : 'Failed to load workspaces'}
          />
        ) : workspaces.length === 0 ? (
          <WorkspaceEmptyState 
            hasFilters={Object.values(filters).some(value => 
              value !== undefined && value !== null && value !== ''
            )}
            onClearFilters={handleClearFilters}
          />
        ) : (
          <div className="table-container">
            <div className="table-scroll-wrapper">
              <WorkspaceTable
                workspaces={workspaces}
                onView={(tenantOrWorkspaceId) => setViewingWorkspaceId(tenantOrWorkspaceId)}
                isLoading={isLoading}
                startIndex={itemsPerPage * currentPageIndex}
              />
            </div>
            {/* Pagination */}
            <div className="table-pagination">
              <div className="pagination-info">
      {viewingWorkspaceId && (
        <div className="modal-overlay" onClick={() => setViewingWorkspaceId(null)}>
          <div className="modal-content" onClick={(e) => e.stopPropagation()}>
            <h3 className="modal-title">Workspace Details</h3>
            {viewLoading ? (
              <div className="py-4">Loading...</div>
            ) : viewingWorkspace ? (
              <div className="space-y-2">
                <div><strong>ID:</strong> {(viewingWorkspace as any).id ?? viewingWorkspace.tenantId}</div>
                <div><strong>Tenant ID:</strong> {viewingWorkspace.tenantId}</div>
                <div><strong>Region:</strong> {viewingWorkspace.accessEvoRegion}</div>
                <div><strong>Application Subscription ID:</strong> {viewingWorkspace.applicationSubscriptionId}</div>
                <div><strong>Organization ID:</strong> {viewingWorkspace.organisationId}</div>
                <div><strong>Salesforce ID:</strong> {viewingWorkspace.salesforceId || '-'}</div>
                <div><strong>Inserted:</strong> {viewingWorkspace.insertTimestamp}</div>
                <div><strong>Updated:</strong> {viewingWorkspace.updatedTimestamp}</div>
              </div>
            ) : (
              <div className="py-4 text-red-600">Failed to load workspace details.</div>
            )}
            <div className="modal-actions mt-4">
              <button className="access-btn access-secondary" onClick={() => setViewingWorkspaceId(null)}>Close</button>
            </div>
          </div>
        </div>
      )}
                {workspaces.length > 0
                  ? `Showing ${itemsPerPage * currentPageIndex + 1} to ${itemsPerPage * currentPageIndex + workspaces.length} of ${stats?.total ?? workspaces.length} entries (page ${currentPageIndex + 1})`
                  : 'No entries'}
              </div>
              <div className="pagination-controls">
                <button 
                  className="access-btn access-secondary" 
                  onClick={handlePreviousPage}
                  disabled={currentPageIndex === 0}
                >
                  Previous
                </button>
                <button 
                  className="access-btn access-secondary"
                  onClick={handleNextPage}
                  disabled={!nextPageToken}
                >
                  Next
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

    </div>
  );
}
